'use client';

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { API_ENDPOINTS } from '../config/api-config';
import axios from '../utils/axios-config';

/**
 * Hook for badge-related operations
 */
export function useBadges() {
  const queryClient = useQueryClient();

  // Get all badges
  const {
    data: allBadges,
    isLoading: isLoadingAll,
    error: allBadgesError,
  } = useQuery({
    queryKey: ['badges', 'all'],
    queryFn: async () => {
      const response = await axios.get(API_ENDPOINTS.BADGES.GET_ALL);
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Get badge by slug
  const getBadgeBySlug = (slug) => {
    return useQuery({
      queryKey: ['badges', 'badge', slug],
      queryFn: async () => {
        const response = await axios.get(
          API_ENDPOINTS.BADGES.GET_BY_SLUG(slug)
        );
        return response.data;
      },
      enabled: !!slug,
    });
  };

  return {
    badges: allBadges?.badges || [],
    groupedBadges: allBadges?.groupedBadges || {},
    isLoading: isLoadingAll,
    error: allBadgesError,
    getBadgeBySlug,
  };
}

/**
 * Hook for user badges
 * @param {string} userAddress - The user's wallet address
 * @param {object} options - Hook options
 * @param {boolean} options.syncOnMount - If true, sync badges on first fetch (checks for any earned but unawarded badges)
 */
export function useUserBadges(userAddress, options = {}) {
  const { syncOnMount = false } = options;
  const queryClient = useQueryClient();

  // Get user's badges (optionally with sync on first load)
  const {
    data: userBadgesData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['badges', 'user', userAddress, syncOnMount ? 'sync' : 'nosync'],
    queryFn: async () => {
      if (!userAddress) return null;
      // Use sync endpoint for initial load if syncOnMount is true
      const endpoint = syncOnMount
        ? API_ENDPOINTS.BADGES.GET_USER_BADGES_WITH_SYNC(userAddress)
        : API_ENDPOINTS.BADGES.GET_USER_BADGES(userAddress);
      const response = await axios.get(endpoint);
      return response.data;
    },
    enabled: !!userAddress,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });

  // Manual sync mutation - checks for any earned badges the user doesn't have yet
  const syncBadgesMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(
        API_ENDPOINTS.BADGES.SYNC_USER_BADGES(userAddress)
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidate badge queries to refresh the UI
      queryClient.invalidateQueries({
        queryKey: ['badges', 'user', userAddress],
      });
      return data;
    },
  });

  // Toggle featured badge
  const toggleFeaturedMutation = useMutation({
    mutationFn: async ({ badgeSlug }) => {
      const response = await axios.post(
        API_ENDPOINTS.BADGES.TOGGLE_FEATURED(badgeSlug),
        { userAddress }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['badges', 'user', userAddress],
      });
    },
  });

  // Set title badge
  const setTitleMutation = useMutation({
    mutationFn: async ({ badgeSlug }) => {
      const response = await axios.post(
        API_ENDPOINTS.BADGES.SET_TITLE(badgeSlug),
        { userAddress }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['badges', 'user', userAddress],
      });
    },
  });

  // Remove title badge
  const removeTitleMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(API_ENDPOINTS.BADGES.REMOVE_TITLE, {
        userAddress,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['badges', 'user', userAddress],
      });
    },
  });

  return {
    badges: userBadgesData?.badges || [],
    featuredBadges: userBadgesData?.featuredBadges || [],
    titleBadge: userBadgesData?.titleBadge || null,
    total: userBadgesData?.total || 0,
    // Sync info (if sync was performed on fetch)
    syncInfo: userBadgesData?.sync || null,
    isLoading,
    error,
    refetch,
    // Manual sync function
    syncBadges: syncBadgesMutation.mutate,
    syncBadgesAsync: syncBadgesMutation.mutateAsync,
    isSyncing: syncBadgesMutation.isPending,
    syncResult: syncBadgesMutation.data,
    toggleFeatured: toggleFeaturedMutation.mutate,
    setTitle: setTitleMutation.mutate,
    removeTitle: removeTitleMutation.mutate,
    isToggling: toggleFeaturedMutation.isPending,
    isSettingTitle: setTitleMutation.isPending,
  };
}

/**
 * Hook for user badges by OID
 */
export function useUserBadgesByOid(oid) {
  const {
    data: userBadgesData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['badges', 'user', 'oid', oid],
    queryFn: async () => {
      if (!oid) return null;
      const response = await axios.get(
        API_ENDPOINTS.BADGES.GET_USER_BADGES_BY_OID(oid)
      );
      return response.data;
    },
    enabled: !!oid,
    staleTime: 2 * 60 * 1000,
  });

  return {
    badges: userBadgesData?.badges || [],
    featuredBadges: userBadgesData?.featuredBadges || [],
    titleBadge: userBadgesData?.titleBadge || null,
    total: userBadgesData?.total || 0,
    isLoading,
    error,
  };
}

/**
 * Get tier styling for badges
 */
export const getBadgeTierStyles = (tier) => {
  switch (tier) {
    case 'gold':
      return {
        bg: 'bg-gradient-to-br from-yellow-400 to-amber-600',
        border: 'border-yellow-400/50',
        text: 'text-yellow-100',
        glow: 'shadow-yellow-500/30',
      };
    case 'silver':
      return {
        bg: 'bg-gradient-to-br from-slate-300 to-slate-500',
        border: 'border-slate-400/50',
        text: 'text-slate-100',
        glow: 'shadow-slate-400/30',
      };
    case 'special':
      return {
        bg: 'bg-gradient-to-br from-violet-500 to-purple-700',
        border: 'border-violet-400/50',
        text: 'text-violet-100',
        glow: 'shadow-violet-500/30',
      };
    case 'bronze':
    default:
      return {
        bg: 'bg-gradient-to-br from-orange-400 to-amber-700',
        border: 'border-orange-400/50',
        text: 'text-orange-100',
        glow: 'shadow-orange-500/30',
      };
  }
};

/**
 * Get group display name
 */
export const getBadgeGroupName = (group) => {
  const names = {
    'getting-started': 'Getting Started',
    community: 'Community',
    posting: 'Posting',
    'trust-level': 'Trust Level',
    blockchain: 'Blockchain',
    special: 'Special',
    anniversary: 'Anniversary',
  };
  return names[group] || group;
};
